<template>
  <div class="question-bank-analytics">
    <div class="page-header mb-4">
      <div>
        <h1 class="page-title">
          <i class="bi bi-bar-chart-line me-2"></i>
          题库分析
        </h1>
        <p class="page-subtitle mb-0">
          按已完成刷题会话统计，两份排行可分别选择滚动时间范围。
        </p>
      </div>
      <button class="btn btn-light" :disabled="loading" @click="loadAnalytics">
        <span
          v-if="loading"
          class="spinner-border spinner-border-sm me-2"
          aria-hidden="true"
        ></span>
        <i v-else class="bi bi-arrow-clockwise me-2"></i>
        刷新
      </button>
    </div>

    <div
      v-if="loading && !analytics"
      class="state-card text-center"
      data-testid="analytics-loading"
    >
      <div class="spinner-border text-primary mb-3" role="status">
        <span class="visually-hidden">正在加载题库分析</span>
      </div>
      <p class="text-muted mb-0">正在加载题库分析数据...</p>
    </div>

    <div v-else-if="error" class="state-card text-center" role="alert">
      <i class="bi bi-exclamation-circle text-danger state-icon"></i>
      <p class="mb-3">{{ error }}</p>
      <button class="btn btn-outline-primary" @click="loadAnalytics">
        重新加载
      </button>
    </div>

    <template v-else-if="analytics">
      <section
        class="analytics-card mb-4"
        aria-labelledby="popular-question-banks-title"
      >
        <div class="card-heading">
          <div>
            <h2 id="popular-question-banks-title">最受欢迎的题库</h2>
            <p>按已完成会话数排序；同一用户的多次完成分别计数。</p>
          </div>
          <div class="heading-actions">
            <label for="popular-period" class="visually-hidden"
              >题库排行时间范围</label
            >
            <select
              id="popular-period"
              v-model="popularPeriod"
              class="form-select form-select-sm period-select"
              data-testid="popular-period"
              :disabled="loading"
              @change="loadAnalytics"
            >
              <option value="1">最近 1 天</option>
              <option value="7">最近 7 天</option>
              <option value="30">最近 30 天</option>
              <option value="all">全部时间</option>
            </select>
            <span class="badge text-bg-primary"
              >Top {{ analytics.popularQuestionBanks.length }}</span
            >
          </div>
        </div>

        <div
          v-if="analytics.popularQuestionBanks.length === 0"
          class="empty-state"
        >
          <i class="bi bi-inbox"></i>
          <p>暂无已完成会话，不能生成题库使用排名。</p>
        </div>
        <div v-else class="table-responsive">
          <table class="table align-middle mb-0">
            <thead>
              <tr>
                <th scope="col">排名</th>
                <th scope="col">题库</th>
                <th scope="col">领域</th>
                <th scope="col" class="text-end">已完成会话</th>
                <th scope="col" class="text-end">独立用户</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(bank, index) in analytics.popularQuestionBanks"
                :key="bank.questionBankId"
              >
                <td>
                  <span class="rank-badge">{{ index + 1 }}</span>
                </td>
                <td>
                  <div class="fw-semibold">{{ bank.subdomain }}</div>
                  <small class="text-muted">ID {{ bank.questionBankId }}</small>
                </td>
                <td>{{ bank.domain }}</td>
                <td class="text-end fw-semibold">
                  {{ bank.completedSessionCount }}
                </td>
                <td class="text-end">{{ bank.uniqueUserCount }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <section
        class="analytics-card"
        aria-labelledby="error-prone-questions-title"
      >
        <div class="card-heading">
          <div>
            <h2 id="error-prone-questions-title">最容易出错的题目</h2>
            <p>错误率 = 错误答案数 ÷ 已提交答案数；同时展示样本量。</p>
          </div>
          <div class="heading-actions">
            <label for="error-prone-period" class="visually-hidden"
              >易错题排行时间范围</label
            >
            <select
              id="error-prone-period"
              v-model="errorPronePeriod"
              class="form-select form-select-sm period-select"
              data-testid="error-prone-period"
              :disabled="loading"
              @change="loadAnalytics"
            >
              <option value="1">最近 1 天</option>
              <option value="7">最近 7 天</option>
              <option value="30">最近 30 天</option>
              <option value="all">全部时间</option>
            </select>
            <span class="badge text-bg-danger"
              >Top {{ analytics.errorProneQuestions.length }}</span
            >
          </div>
        </div>

        <div
          v-if="analytics.errorProneQuestions.length === 0"
          class="empty-state"
        >
          <i class="bi bi-inbox"></i>
          <p>暂无已完成会话的答题记录，不能生成易错题排名。</p>
        </div>
        <div v-else class="table-responsive">
          <table class="table align-middle mb-0">
            <thead>
              <tr>
                <th scope="col">排名</th>
                <th scope="col">题目</th>
                <th scope="col">题库</th>
                <th scope="col" class="text-end">错误 / 答题</th>
                <th scope="col" class="text-end">错误率</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(item, index) in analytics.errorProneQuestions"
                :key="item.questionId"
              >
                <td>
                  <span class="rank-badge rank-danger">{{ index + 1 }}</span>
                </td>
                <td>
                  <router-link
                    class="question-link question-text"
                    :data-testid="`edit-error-prone-question-${item.questionId}`"
                    :title="`查看并编辑：${item.question}`"
                    :to="{
                      name: 'QuestionEdit',
                      params: {
                        bankId: item.questionBankId,
                        questionId: item.questionId,
                      },
                    }"
                  >
                    {{ item.question }}
                  </router-link>
                  <small class="text-muted"
                    >题目 ID {{ item.questionId }} ·
                    {{ questionTypeLabel(item.questionType) }}</small
                  >
                </td>
                <td>{{ item.questionBankName }}</td>
                <td class="text-end">
                  {{ item.incorrectAnswerCount }} / {{ item.answerCount }}
                </td>
                <td class="text-end">
                  <span class="error-rate">{{
                    formatRate(item.errorRate)
                  }}</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>
    </template>
  </div>
</template>

<script setup lang="ts">
import { onMounted, ref } from 'vue'
import {
  adminApi,
  type QuestionBankAnalytics,
  type QuestionBankAnalyticsPeriods,
} from '@/api/admin'

type AnalyticsPeriod = 'all' | '1' | '7' | '30'

const analytics = ref<QuestionBankAnalytics | null>(null)
const loading = ref(false)
const error = ref('')
const popularPeriod = ref<AnalyticsPeriod>('all')
const errorPronePeriod = ref<AnalyticsPeriod>('all')

const questionTypeLabels: Record<string, string> = {
  singleChoice: '单选题',
  multipleChoice: '多选题',
  trueFalse: '判断题',
  fillBlank: '填空题',
  essay: '简答题',
  calculation: '计算题',
}

const questionTypeLabel = (type: string) => questionTypeLabels[type] || type
const formatRate = (rate: number) => `${rate.toFixed(2)}%`

const selectedPeriods = (): QuestionBankAnalyticsPeriods => ({
  ...(popularPeriod.value === 'all'
    ? {}
    : { popularDays: Number(popularPeriod.value) as 1 | 7 | 30 }),
  ...(errorPronePeriod.value === 'all'
    ? {}
    : { errorProneDays: Number(errorPronePeriod.value) as 1 | 7 | 30 }),
})

const loadAnalytics = async () => {
  loading.value = true
  error.value = ''
  try {
    analytics.value = await adminApi.getQuestionBankAnalytics(selectedPeriods())
  } catch (loadError) {
    console.error('加载题库分析失败:', loadError)
    error.value = '题库分析数据加载失败，请稍后重试。'
  } finally {
    loading.value = false
  }
}

onMounted(loadAnalytics)
</script>

<style scoped>
.question-bank-analytics {
  color: #1f2937;
}

.page-header {
  align-items: center;
  background: linear-gradient(135deg, #2563eb 0%, #4f46e5 100%);
  border-radius: 15px;
  box-shadow: 0 10px 30px rgba(37, 99, 235, 0.25);
  color: white;
  display: flex;
  justify-content: space-between;
  padding: 2rem;
}

.page-title {
  font-size: 2rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
}

.page-subtitle {
  color: rgba(255, 255, 255, 0.82);
}

.analytics-card,
.state-card {
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 14px;
  box-shadow: 0 6px 20px rgba(15, 23, 42, 0.06);
  overflow: hidden;
}

.state-card {
  padding: 4rem 2rem;
}

.state-icon {
  display: block;
  font-size: 3rem;
  margin-bottom: 1rem;
}

.card-heading {
  align-items: center;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  justify-content: space-between;
  padding: 1.25rem 1.5rem;
}

.card-heading h2 {
  font-size: 1.15rem;
  font-weight: 650;
  margin: 0 0 0.25rem;
}

.card-heading p {
  color: #64748b;
  font-size: 0.9rem;
  margin: 0;
}

.heading-actions {
  align-items: center;
  display: flex;
  gap: 0.75rem;
}

.period-select {
  min-width: 8rem;
}

.table th {
  background: #f8fafc;
  color: #475569;
  font-size: 0.8rem;
  font-weight: 650;
  letter-spacing: 0.02em;
  white-space: nowrap;
}

.table td,
.table th {
  padding: 0.9rem 1.25rem;
}

.rank-badge {
  align-items: center;
  background: #dbeafe;
  border-radius: 999px;
  color: #1d4ed8;
  display: inline-flex;
  font-weight: 700;
  height: 2rem;
  justify-content: center;
  width: 2rem;
}

.rank-danger {
  background: #fee2e2;
  color: #b91c1c;
}

.question-text {
  display: -webkit-box;
  max-width: 34rem;
  overflow: hidden;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
}

.question-link {
  color: #1d4ed8;
  font-weight: 600;
  text-decoration: none;
}

.question-link:hover,
.question-link:focus-visible {
  text-decoration: underline;
}

.error-rate {
  background: #fee2e2;
  border-radius: 999px;
  color: #b91c1c;
  display: inline-block;
  font-weight: 700;
  min-width: 5rem;
  padding: 0.3rem 0.65rem;
  text-align: center;
}

.empty-state {
  color: #64748b;
  padding: 3rem 1.5rem;
  text-align: center;
}

.empty-state i {
  display: block;
  font-size: 2.5rem;
  margin-bottom: 0.75rem;
}

.empty-state p {
  margin: 0;
}

@media (max-width: 767px) {
  .page-header {
    align-items: flex-start;
    flex-direction: column;
    gap: 1.25rem;
  }

  .page-title {
    font-size: 1.6rem;
  }

  .card-heading {
    align-items: flex-start;
    flex-direction: column;
    gap: 1rem;
  }
}
</style>
